import { css } from "@emotion/react";
import {
  FloatingPortal,
  arrow,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { useContext, useMemo, useRef, useState } from "react";
import transparentBookmarkIcon from "../../../assets/transparent-bookmark.svg";
import { trpc } from "../../../utils/trpc";
import { BookmarkContext } from "../../contexts/WithBookmarks/BookmarkContext";
import type { ContentsItem } from "../../contexts/WithPublication/PublicationContext";
import { PublicationContext } from "../../contexts/WithPublication/PublicationContext";
import { LoadingSpinner } from "../loading";
import { BookmarkIcon } from "./BookmarkIcon";
import { CreateOrEditCollectionModal } from "./CreateOrEditCollectionModal";

const getHeadingHierarchyForSection = (
  items: ContentsItem[],
  targetSectionId: string,
): string[] => {
  const findPath = (nodes: ContentsItem[], path: string[]): string[] => {
    for (const node of nodes) {
      const nextPath = node.label ? [...path, node.label] : path;

      if (node.id === targetSectionId) {
        return nextPath;
      }

      if (node.children?.length) {
        const childPath = findPath(node.children, nextPath);
        if (childPath.length) return childPath;
      }
    }
    return [];
  };

  return findPath(items, []);
};

interface BookmarkButtonProps {
  sectionId?: string;
  sectionLabel?: string;
  annotationsOpen?: boolean;
  articleSlug?: string;
  contents?: ContentsItem[];
}

export const BookmarkButton = ({
  sectionId: propSectionId,
  sectionLabel: propSectionLabel,
  annotationsOpen,
  articleSlug,
  contents: propContents,
}: BookmarkButtonProps = {}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isCreateCollectionOpen, setIsCreateCollectionOpen] = useState(false);
  const {
    publication,
    currentSection: pubCurrentSection,
    contents: pubContents,
  } = useContext(PublicationContext);
  const { bookmarksByPublication } = useContext(BookmarkContext);

  const { data: frjBookmarks } = trpc.getFRJBookmarks.useQuery(undefined, {
    enabled: !publication?.id && !!articleSlug,
  });

  const contents = propContents ?? pubContents;

  const currentSection = pubCurrentSection;
  const activeSectionId = propSectionId ?? currentSection?.id;
  const activeSectionLabel = propSectionLabel ?? currentSection?.label;
  const activeBookmarks = useMemo(
    () => (publication ? bookmarksByPublication : frjBookmarks) ?? [],
    [publication, bookmarksByPublication, frjBookmarks],
  );

  const utils = trpc.useUtils();

  const { data: collections = [], isLoading: isCollectionsLoading } =
    trpc.getCollections.useQuery(undefined, {
      enabled: isPopupOpen,
    });

  const invalidateBookmarks = async () => {
    await utils.getCollections.invalidate();
    if (articleSlug) {
      await utils.getBookmarksByArticle.invalidate({ articleSlug });
      await utils.getFRJBookmarks.invalidate();
    } else if (publication?.id) {
      await utils.getBookmarksByPublication.invalidate({
        publicationId: publication.id,
      });
    }
  };

  const addBookmark = trpc.addBookmark.useMutation({
    onSuccess: async () => {
      await invalidateBookmarks();
    },
  });

  const removeBookmark = trpc.removeBookmark.useMutation({
    onSuccess: async () => {
      await invalidateBookmarks();
    },
  });

  const canSaveBookmark = Boolean(
    (articleSlug || publication?.id) && activeSectionId && activeSectionLabel,
  );

  const currentSectionBookmark = useMemo(() => {
    if (!activeSectionId) return undefined;
    return activeBookmarks.find(
      (bookmark) => bookmark.sectionId === activeSectionId,
    );
  }, [activeSectionId, activeBookmarks]);

  const currentSectionCollectionColours = useMemo(() => {
    if (!currentSectionBookmark) return [];
    return currentSectionBookmark.collections.map(
      (col) => col.collection.colour,
    );
  }, [currentSectionBookmark]);

  const currentSectionCollectionIds = useMemo(() => {
    if (!currentSectionBookmark) return new Set<number>();
    return new Set(
      currentSectionBookmark.collections.map((col) => col.collection.id),
    );
  }, [currentSectionBookmark]);

  const isBookmarked = !!currentSectionBookmark;

  const arrowRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, context, middlewareData, placement } =
    useFloating({
      open: isPopupOpen,
      onOpenChange: setIsPopupOpen,
      placement: "bottom",
      strategy: "fixed",
      middleware: [
        offset({
          mainAxis: 12,
          crossAxis: 100,
        }),
        flip(),
        shift({ padding: 8 }),
        arrow({ element: arrowRef }),
      ],
    });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const toggleBookmarkInCollection = (collectionId?: number) => {
    if (
      (!articleSlug && !publication?.id) ||
      !activeSectionId ||
      !activeSectionLabel
    ) {
      return;
    }

    // If this section is already bookmarked in this collection, remove it
    if (
      collectionId &&
      currentSectionBookmark &&
      currentSectionCollectionIds.has(collectionId)
    ) {
      removeBookmark.mutate({
        collectionId,
        bookmarkId: currentSectionBookmark.id,
      });
      return;
    }

    const parentSectionHeadings = contents
      ? getHeadingHierarchyForSection(contents, activeSectionId)
      : [];

    const mostNestedSubsectionTitle =
      parentSectionHeadings.at(-1) ?? activeSectionLabel;

    addBookmark.mutate({
      ...(articleSlug ? { articleSlug } : { publicationId: publication!.id }),
      sectionId: activeSectionId,
      sectionTitle: mostNestedSubsectionTitle,
      collectionId,
      parentSections: parentSectionHeadings,
    });
  };

  const { x: arrowX } = middlewareData.arrow || {};
  const isFlipped = placement.startsWith("top");

  const colours = currentSectionCollectionColours;
  const total = colours.length;

  return (
    <>
      <button
        ref={refs.setReference}
        css={css`
          border: none;
          background: none;
          cursor: pointer;
          display: grid;
          gap: 1px;
          transition: transform 0.15s ease;
          ${annotationsOpen ? "transform: translateX(-32px);" : ""}
          grid-template-columns: ${colours.length === 0 || colours.length === 1
            ? "1fr"
            : "repeat(2, 1fr)"};
          padding: 0;
          width: fit-content;
          z-index: 2;
        `}
        type="button"
        aria-label={isBookmarked ? "Manage bookmark" : "Add bookmark"}
        {...getReferenceProps()}
      >
        {isBookmarked ? (
          (() => {
            //  If we have MORE than 4, swap the 4th icon for a +
            const showPlus = currentSectionCollectionColours.length > 4;

            // If we show plus, only 3 icons will be shown, otherwise show up to 4
            const iconsToShow = showPlus
              ? colours.slice(0, 3)
              : colours.slice(0, 4);

            return (
              <>
                {iconsToShow.map((colour, index) => (
                  <div
                    key={index}
                    css={css`
                      ${index === 2 && total === 3
                        ? "grid-column: span 2;"
                        : ""}
                      display: flex;
                      justify-content: center;
                    `}
                  >
                    <BookmarkIcon
                      colour={colour}
                      size={total === 1 ? "large" : "medium"}
                    />
                  </div>
                ))}

                {showPlus && (
                  <div
                    css={css`
                      align-items: center;
                      color: #283472;
                      display: flex;
                      font-size: 16px;
                      font-style: normal;
                      height: 14px;
                      justify-content: center;
                      width: 16px;
                      &::after {
                        content: "+";
                      }
                    `}
                  />
                )}
              </>
            );
          })()
        ) : (
          <img
            src={transparentBookmarkIcon}
            alt="Add bookmark"
            css={css`
              min-height: 26px;
              min-width: 26px;
            `}
          />
        )}
      </button>

      {isPopupOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            css={(theme) => css`
              background-color: ${theme.colours.uIGrey.light._5};
              border: 1px solid ${theme.colours.uIGrey.light._20};
              border-radius: 12px;
              box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
              display: ${isCreateCollectionOpen ? "none" : "flex"};
              flex-direction: column;
              gap: 10px;
              min-width: 170px;
              padding: 10px;
              z-index: 10;
            `}
          >
            <div
              ref={arrowRef}
              style={{
                position: "absolute",
                left: arrowX != null ? `${arrowX}px` : "",
                ...(isFlipped ? { bottom: "-6px" } : { top: "-6px" }),
              }}
              css={(theme) => css`
                background-color: ${theme.colours.uIGrey.light._5};
                ${isFlipped
                  ? `border-right: 1px solid ${theme.colours.uIGrey.light._20}; border-bottom: 1px solid ${theme.colours.uIGrey.light._20};`
                  : `border-left: 1px solid ${theme.colours.uIGrey.light._20}; border-top: 1px solid ${theme.colours.uIGrey.light._20};`}
                height: 12px;
                transform: rotate(45deg);
                width: 12px;
              `}
            />

            <div
              css={css`
                align-items: center;
                display: flex;
                justify-content: space-between;
              `}
            >
              <span
                css={(theme) => css`
                  color: ${theme.colours.accent.dark._50};
                  font-size: 12px;
                  font-weight: 500;
                `}
              >
                Add bookmark to:
              </span>

              <CreateOrEditCollectionModal
                isOpen={isCreateCollectionOpen}
                onClose={() => {
                  setIsCreateCollectionOpen(false);
                }}
                renderButton={() => (
                  <button
                    css={(theme) => css`
                      border: 1px solid ${theme.colours.accent.light._20};
                      border-radius: 6px;
                      background-color: ${theme.colours.uIGrey.light._10};
                      color: ${theme.colours.primary.dark._30};
                      cursor: pointer;
                      font-size: 11px;
                      font-weight: 600;
                      padding: 4px 8px;
                      &:hover {
                        background-color: ${theme.colours.uIGrey.light._20};
                      }
                    `}
                    onClick={() => {
                      setIsCreateCollectionOpen(true);
                    }}
                    type="button"
                  >
                    + Add collection
                  </button>
                )}
              />
            </div>

            {!canSaveBookmark && (
              <LoadingSpinner
                css={css`
                  height: auto;
                  min-height: 40px;
                `}
              />
            )}

            {canSaveBookmark && isCollectionsLoading && (
              <LoadingSpinner
                css={css`
                  height: auto;
                  min-height: 40px;
                `}
              />
            )}

            {canSaveBookmark &&
              !isCollectionsLoading &&
              collections.length === 0 && (
                <button
                  css={(theme) => css`
                    border: 1px solid ${theme.colours.accent.light._20};
                    border-radius: 6px;
                    background-color: ${theme.colours.uIGrey.light._10};
                    color: ${theme.colours.primary.dark._30};
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 6px 10px;
                    text-align: left;
                    z-index: 2;
                    &:hover {
                      background-color: ${theme.colours.uIGrey.light._20};
                    }
                    &:disabled {
                      cursor: not-allowed;
                      opacity: 0.5;
                      pointer-events: none;
                    }
                  `}
                  disabled={addBookmark.isLoading || removeBookmark.isLoading}
                  onClick={() => toggleBookmarkInCollection()}
                  type="button"
                >
                  <span
                    css={css`
                      align-items: center;
                      display: inline-flex;
                      gap: 6px;
                    `}
                  >
                    <BookmarkIcon colour="#283472" />
                    Default collection
                  </span>
                </button>
              )}

            {canSaveBookmark &&
              [...collections]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((collection) => (
                  <button
                    key={collection.id}
                    css={(theme) => css`
                      align-items: center;
                      background-color: ${theme.colours.uIGrey.light._10};
                      border: 2px solid
                        ${currentSectionCollectionIds.has(collection.id)
                          ? theme.colours.primary.dark._30
                          : theme.colours.accent.light._20};
                      border-radius: 6px;
                      color: ${theme.colours.primary.dark._30};
                      cursor: pointer;
                      display: flex;
                      font-size: 12px;
                      font-weight: 600;
                      padding: 6px 10px;
                      text-align: left;
                      transition: border-color 0.15s ease;
                      z-index: 2;
                      &:hover {
                        background-color: ${theme.colours.uIGrey.light._20};
                      }
                      &:disabled {
                        cursor: not-allowed;
                        opacity: 0.5;
                        pointer-events: none;
                      }
                    `}
                    disabled={addBookmark.isLoading || removeBookmark.isLoading}
                    onClick={() => toggleBookmarkInCollection(collection.id)}
                    type="button"
                  >
                    <span
                      css={css`
                        align-items: center;
                        display: inline-flex;
                        gap: 6px;
                      `}
                    >
                      <BookmarkIcon colour={collection.colour} />
                      {collection.name}
                    </span>
                  </button>
                ))}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
